import React, { useState, useEffect } from 'react';
import '../ShareLinkCreator.css';

const LABEL_COLORS = {
  'experiences': '#3b725f',
  'preferences': '#7f5f43',
  'relationships': '#bd7b69',
  'memories': '#cccac5',
  'thoughts': '#D8BFD8',
  'Work': '#FF6B6B',
  'Family': '#4ECDC4',
  'Personal': '#45B7D1',
  'Private': '#96CEB4',
  'Health': '#FECA57',
  'Finance': '#48DBFB',
  'Education': '#FF9FF3',
  'Hobby': '#54A0FF',
  'Travel': '#FD79A8',
  'Social': '#A29BFE',
  'Goals': '#6C5CE7',
  'Ideas': '#FFC048'
};

function ShareLinkCreator({ isOpen, onClose, onCreate, token, api, memories }) {
  const [includeMemories, setIncludeMemories] = useState(true);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [creating, setCreating] = useState(false);
  const [memoryCount, setMemoryCount] = useState(0);
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [maxTier, setMaxTier] = useState(3);

  // Extract unique categories from memories
  useEffect(() => {
    if (memories && memories.length > 0) {
      const categories = new Set();
      let count = 0;
      
      memories.forEach(memory => {
        // Add category
        if (memory.category && memory.category !== 'unknown') {
          categories.add(memory.category);
        }
        // Add labels
        if (memory.labels && memory.labels.length > 0) {
          memory.labels.forEach(label => categories.add(label));
        }
      });
      
      const sortedCategories = Array.from(categories).sort();
      setAvailableCategories(sortedCategories);
      
      // By default, select all categories
      if (selectAll) {
        setSelectedCategories(sortedCategories);
        setMemoryCount(memories.length);
      }
    }
  }, [memories, selectAll]);

  // Update memory count when categories change
  useEffect(() => {
    if (!memories) {
      setMemoryCount(0);
      return;
    }
    
    if (selectedCategories.length === 0 || !includeMemories) {
      setMemoryCount(0);
      return;
    }
    
    // Count memories that match selected categories
    const matchingMemories = memories.filter(memory => {
      const memoryCategory = memory.category;
      const memoryLabels = memory.labels || [];
      
      // Check if memory matches any selected category
      const categoryMatch = selectedCategories.includes(memoryCategory);
      const labelMatch = memoryLabels.some(label => selectedCategories.includes(label));
      
      return categoryMatch || labelMatch;
    });
    
    setMemoryCount(matchingMemories.length);
  }, [selectedCategories, memories, includeMemories]);

  const toggleCategory = (category) => {
    if (selectedCategories.includes(category)) {
      setSelectedCategories(prev => prev.filter(c => c !== category));
      setSelectAll(false);
    } else {
      const newSelected = [...selectedCategories, category];
      setSelectedCategories(newSelected);
      setSelectAll(newSelected.length === availableCategories.length);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCategories([]);
      setSelectAll(false);
    } else {
      setSelectedCategories(availableCategories);
      setSelectAll(true);
    }
  };

  const handleCreate = async () => {
    setCreating(true);
    setCreatedLink(null);
    
    try {
      const response = await fetch(`${api}/create_share_link`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ 
          include_memories: includeMemories,
          selected_categories: selectAll || !includeMemories ? null : selectedCategories,
          expires_at: null,
          max_tier: maxTier
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${window.location.origin}/share/${data.share_id}`;
        setCreatedLink(fullUrl);
        
        if (onCreate) {
          onCreate(data);
        }
      } else {
        console.error("Failed to create share link");
      }
    } catch (error) {
      console.error("Error creating share link:", error);
    } finally {
      setCreating(false);
    }
  };

  const copyToClipboard = () => {
    if (createdLink) {
      navigator.clipboard.writeText(createdLink).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const handleClose = () => {
    setCreatedLink(null);
    setCopied(false);
    setSelectedCategories(availableCategories);
    setSelectAll(true);
    setIncludeMemories(true);
    setMaxTier(3);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="share-link-creator-overlay">
      <div className="share-link-creator">
        <div className="share-link-header">
          <h2>Create Legacy Mode Share Link</h2>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        {!createdLink ? (
          <>
            <div className="share-link-content">
              <div className="include-memories-section">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={includeMemories}
                    onChange={(e) => setIncludeMemories(e.target.checked)}
                  />
                  <span>Include Memories in Share</span>
                </label>
                <p className="help-text">
                  {includeMemories 
                    ? `${memoryCount} memories will be included`
                    : "No memories will be shared (blank Legacy mode)"}
                </p>
              </div>

              {includeMemories && (
                <div className="category-selection-section">
                  <div className="section-header">
                    <h3>Select Memory Categories to Share</h3>
                    <button 
                      className="select-all-button"
                      onClick={handleSelectAll}
                    >
                      {selectAll ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  
                  <div className="category-grid">
                    {availableCategories.length > 0 ? (
                      availableCategories.map(category => (
                        <button
                          key={category}
                          className={`category-chip ${selectedCategories.includes(category) ? 'selected' : ''}`}
                          onClick={() => toggleCategory(category)}
                          style={{
                            backgroundColor: selectedCategories.includes(category) 
                              ? (LABEL_COLORS[category] || '#666')
                              : 'transparent',
                            borderColor: LABEL_COLORS[category] || '#666',
                            color: selectedCategories.includes(category) 
                              ? 'white' 
                              : (LABEL_COLORS[category] || '#666')
                          }}
                        >
                          {category}
                        </button>
                      ))
                    ) : (
                      <p className="no-categories">No categories found in your memories</p>
                    )}
                  </div>

                  {selectedCategories.length === 0 && (
                    <p className="warning-text">
                      ⚠️ No categories selected - no memories will be included
                    </p>
                  )}
                </div>
              )}

              {includeMemories && (
                <div className="tier-selection-section" style={{ marginTop: 16 }}>
                  <h3 style={{ marginBottom: 8 }}>Maximum Privacy Tier to Share</h3>
                  <p className="help-text" style={{ marginBottom: 8 }}>
                    Only memories at or below this tier will be included
                  </p>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {[
                      { tier: 1, label: 'Public', color: '#4caf50' },
                      { tier: 2, label: 'Community', color: '#8bc34a' },
                      { tier: 3, label: 'Inner Circle', color: '#e8c77b' },
                      { tier: 4, label: 'Family', color: '#ff9800' },
                      { tier: 5, label: 'All (Vault)', color: '#f44336' },
                    ].map(t => (
                      <button
                        key={t.tier}
                        type="button"
                        onClick={() => setMaxTier(t.tier)}
                        style={{
                          flex: 1, padding: '6px 2px', borderRadius: 6, cursor: 'pointer',
                          fontSize: 10, fontWeight: maxTier === t.tier ? 700 : 400,
                          textAlign: 'center',
                          background: maxTier === t.tier ? `${t.color}22` : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${maxTier === t.tier ? t.color : 'rgba(255,255,255,0.12)'}`,
                          color: maxTier === t.tier ? t.color : '#999',
                          transition: 'all 0.15s',
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="share-info">
                <h3>What will be shared:</h3>
                <ul>
                  <li>Legacy mode personality and voice</li>
                  <li>Your avatar and environment settings</li>
                  {includeMemories && selectedCategories.length > 0 && (
                    <li>
                      {memoryCount} memories from {selectedCategories.length} categories
                    </li>
                  )}
                </ul>
                <p className="privacy-note">
                  🔒 Share links are secure and can be deactivated anytime
                </p>
              </div>
            </div>

            <div className="share-link-footer">
              <button 
                className="cancel-button"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button 
                className="create-button"
                onClick={handleCreate}
                disabled={creating || (includeMemories && selectedCategories.length === 0)}
              >
                {creating ? 'Creating...' : 'Create Share Link'}
              </button>
            </div>
          </>
        ) : (
          <div className="share-link-success">
            <div className="success-icon">✓</div>
            <h3>Share Link Created Successfully!</h3>
            
            <div className="link-display">
              <input 
                type="text" 
                value={createdLink} 
                readOnly 
                className="link-input"
              />
              <button 
                className="copy-button"
                onClick={copyToClipboard}
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>

            <div className="link-details">
              <p>This link includes:</p>
              <ul>
                <li>Legacy mode with your personality</li>
                {includeMemories && (
                  <li>
                    {memoryCount} memories 
                    {!selectAll && ` from ${selectedCategories.length} selected categories`}
                  </li>
                )}
              </ul>
            </div>

            <div className="share-link-footer">
              <button 
                className="done-button"
                onClick={handleClose}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ShareLinkCreator;
